import {
  BlockObject,
  Configs,
  ContentModelConfigMergedBase,
  getContentViewFunction,
} from '@dudagroup/editor'
import {
  ContentEditAction,
  ContentEditActionEnum,
} from '@dudagroup/editor/lib/client/control/contentReducer'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button, Grid, Message, Panel} from 'rsuite'

import {usePdfParseLazyQuery} from '../../../../api'

/**
 * The media detail view: the regular field panel plus a "create index from PDF"
 * button that fills the search index, alt text and title of every language
 * from the uploaded PDFs (RESNF-1095, ported from snf-website RESNF-1066).
 */
export const getMediaView: getContentViewFunction = (
  content: any,
  handleChange: any,
  disabled: boolean,
  dispatch: React.Dispatch<ContentEditAction>,
  configs: Configs,
  contentModelConfigMerged: ContentModelConfigMergedBase,
  langLaneL,
  langLaneR,
  langUi
) => {
  return (
    <MediaView
      {...{
        content,
        handleChange,
        disabled,
        dispatch,
        configs,
        contentModelConfigMerged,
        langLaneL,
        langLaneR,
        langUi,
      }}></MediaView>
  )
}

interface MediaViewProps {
  content: any
  handleChange: any
  disabled: boolean
  dispatch: React.Dispatch<ContentEditAction>
  configs: Configs
  contentModelConfigMerged: ContentModelConfigMergedBase
  langLaneL: string
  langLaneR: string
  langUi: string
}

// The site's default language, the source of the alt text and title
// translations. It mirrors `defaultLanguageTag` in packages/api/src/configs.ts;
// a tenant that stores another default in its language settings would need it
// injected through the client settings, as snf-website does for its Italian
// site. `configs.apiConfig.languages.defaultLanguageTag` can't be used: the
// editor overwrites it with the UI language to order the language lanes.
const DEFAULT_LANGUAGE = 'de'

// Measured against the live Trados lookup endpoint: text up to ~130'926 characters
// is translated, from ~130'944 on it silently returns the source text instead.
// The exact threshold shifts a little with the content, so warn from 120'000.
// Matches MAX_LOOKUP_CHARACTERS in packages/api/src/customApi/trados.ts.
const TRADOS_TRANSLATION_CHAR_LIMIT = 120000

/**
 * Thousands separators follow the editor's UI language, not the content's - a
 * hard-coded de-CH showed `695'190` to an English editor who expects `695,190`.
 *
 * The tags are mapped rather than passed through: `toLocaleString('de')` is
 * German (Germany) and groups with a dot, while the Swiss editor wants the
 * apostrophe form.
 */
const NUMBER_LOCALE_BY_UI_LANGUAGE: Record<string, string> = {
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  en: 'en-GB',
}

/**
 * `toLocaleString` groups de-CH/it-CH with an ASCII apostrophe (U+0027), which
 * HTML-escapes to `&#39;` and reads as a stray straight quote. U+2019 is the
 * correct typographic separator, and the other locales are unaffected (fr-CH
 * uses a narrow no-break space, en-GB a comma).
 */
function formatCharCount(value: number, langUi: string): string {
  const locale = NUMBER_LOCALE_BY_UI_LANGUAGE[langUi] ?? 'de-CH'
  return value.toLocaleString(locale).replace(/'/g, '’')
}

/** The three fields `pdfParse` can fill by machine translation. */
type TranslatedField = 'index' | 'altText' | 'title'
interface PendingTranslation {
  field: TranslatedField
  lang: string
}

const FIELD_LABEL_KEY: Record<TranslatedField, string> = {
  index: 'media.fieldIndex',
  altText: 'media.fieldAltText',
  title: 'media.fieldTitle',
}

/**
 * Turns the list of translations that did not come back into messages an editor
 * can act on.
 *
 * Two distinct causes, reported separately so the second is not blamed on the
 * first: an index over the lookup limit cannot be translated at all, which is a
 * property of this document, while anything else missing points at the
 * translation engine. Both name the exact fields and languages - "0 of 2" tells
 * an editor nothing about what to fix.
 */
function buildTranslationWarnings(
  missing: PendingTranslation[],
  indexSourceLength: number,
  sourceLanguage: string,
  langUi: string,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string
): string[] {
  if (missing.length === 0) return []

  const messages: string[] = []
  const overLimit = indexSourceLength > TRADOS_TRANSLATION_CHAR_LIMIT
  const missingIndexLangs = missing.filter(({field}) => field === 'index').map(({lang}) => lang)

  if (overLimit && missingIndexLangs.length > 0) {
    messages.push(
      t(
        'media.indexTooLongForTranslation',
        'The {{lang}} index has {{chars}} characters, over the {{limit}}-character translation limit, so it cannot be machine translated. The index stays empty for {{langs}} - upload a PDF in those languages to fill it.',
        {
          lang: sourceLanguage,
          chars: formatCharCount(indexSourceLength, langUi),
          limit: formatCharCount(TRADOS_TRANSLATION_CHAR_LIMIT, langUi),
          langs: missingIndexLangs.join(', '),
        }
      )
    )
  }

  // Everything the limit does not already explain.
  const unexplained = overLimit ? missing.filter(({field}) => field !== 'index') : missing
  if (unexplained.length > 0) {
    const byField = new Map<TranslatedField, string[]>()
    for (const {field, lang} of unexplained) {
      byField.set(field, [...(byField.get(field) ?? []), lang])
    }
    const details = [...byField.entries()]
      .map(([field, langs]) => `${t(FIELD_LABEL_KEY[field], field)} (${langs.join(', ')})`)
      .join(', ')

    messages.push(
      t(
        'media.indexTranslationsMissing',
        'Trados returned no translation for {{details}}. Check that the translation engine has a machine translation provider attached for these languages.',
        {details}
      )
    )
  }

  return messages
}

function MediaView(props: MediaViewProps) {
  const {
    content,
    disabled,
    dispatch,
    configs,
    contentModelConfigMerged,
    langLaneL,
    langLaneR,
    langUi,
  } = props
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [readErrors, setReadErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [notices, setNotices] = useState<string[]>([])

  const [fetchPdfText] = usePdfParseLazyQuery({fetchPolicy: 'no-cache'})

  const fillIndex = async () => {
    setError(null)
    setReadErrors([])
    setWarnings([])
    setNotices([])

    // Build the public media URL the same way as the media `getPreviewLink`
    // in config.tsx: `${mediaPreviewUrl}/media/${lang}/${id}/${filename}${ext}`.
    // The API falls back to /media-noindex/ itself when the document is not
    // indexable.
    const settings = document.getElementById('settings')
    if (!settings?.textContent) {
      setError(t('media.indexNoSettings', 'Missing client settings.'))
      return
    }
    const {mediaPreviewUrl} = JSON.parse(settings.textContent)

    // `getContentView` isn't handed the record, so read its id from the URL:
    // /content/{type}/edit/{id}/{revision?}
    const recordId = window.location.pathname.match(/\/content\/[^/]+\/edit\/([^/]+)/)?.[1]
    if (!recordId) {
      setError(t('media.indexNoRecord', 'Could not determine the record id.'))
      return
    }

    // `media`, `index`, `altText` and `title` are i18n fields keyed by
    // language, and every configured language can have its own uploaded PDF.
    // Languages come from the global config, never a hardcoded list.
    const mediaByLang: Record<string, any> = content?.media ?? {}
    const indexByLang: Record<string, string> = content?.index ?? {}
    const altTextByLang: Record<string, string> = content?.altText ?? {}
    const titleByLang: Record<string, string> = content?.title ?? {}
    const languages = configs.apiConfig.languages.languages

    const pdfUrls = languages
      .map((language) => {
        const media: {filename?: string; extension?: string; mimeType?: string} | undefined =
          mediaByLang[language.tag]?.media
        if (!media?.filename || !media?.extension) return null

        const isPdf =
          media.mimeType === 'application/pdf' || media.extension.toLowerCase() === '.pdf'
        if (!isPdf) return null

        return {
          lang: language.tag,
          url: `${mediaPreviewUrl}/media/${language.tag}/${recordId}/${media.filename}${media.extension}`,
        }
      })
      .filter((target): target is {lang: string; url: string} => target != null)

    if (pdfUrls.length === 0) {
      setError(t('media.indexNoFile', 'Only PDF files are supported'))
      return
    }

    const nonEmpty = (byLang: Record<string, string>) =>
      languages
        .map((language) => ({lang: language.tag, text: byLang[language.tag] ?? ''}))
        .filter(({text}) => text)

    // Which (field, language) pairs the API is expected to translate. Kept as a
    // list rather than a count so a warning can name exactly what is missing:
    // Trados answers a lookup it cannot serve with an empty result rather than
    // an error, so without this a completely failed run looks like success.
    // The index isn't predicted here: which language a PDF really is in is only
    // known once the API has detected it, so the API reports its own targets.
    const langTags = languages.map((language) => language.tag)
    const pendingFor = (field: TranslatedField, byLang: Record<string, string>) =>
      byLang[DEFAULT_LANGUAGE]
        ? langTags
            .filter((tag) => tag !== DEFAULT_LANGUAGE && !byLang[tag])
            .map((lang) => ({field, lang}))
        : []
    const expected: PendingTranslation[] = [
      ...pendingFor('altText', altTextByLang),
      ...pendingFor('title', titleByLang),
    ]

    setLoading(true)
    try {
      // The PDFs are fetched/parsed and any missing translations of `index`,
      // `altText` and `title` are filled in on the API server via an
      // authenticated GraphQL query, which avoids CORS and keeps
      // pdf-parse/pdfjs out of the browser bundle.
      const {data, error} = await fetchPdfText({
        variables: {
          pdfUrls,
          existingIndex: nonEmpty(indexByLang),
          existingAltText: nonEmpty(altTextByLang),
          existingTitle: nonEmpty(titleByLang),
        },
      })
      if (error || !data) {
        throw new Error(`Failed to parse/translate PDFs: ${error?.message ?? 'unknown error'}`)
      }

      const {results, misplacedPdfs, failedPdfs, indexSourceLang, indexTranslationTargets} =
        data.extensions.pdfParse
      const indexTargets = new Set(indexTranslationTargets)
      expected.push(...indexTranslationTargets.map((lang) => ({field: 'index' as const, lang})))

      // Empty values are never written back - an empty translation would wipe
      // the text an editor typed by hand.
      const received = new Set<string>()
      results.forEach(({lang, index, altText, title}) => {
        if (index) {
          dispatch({type: ContentEditActionEnum.update, path: ['index', lang], value: index})
          // Only count it as a translation when it cannot have come from a PDF.
          if (indexTargets.has(lang)) received.add(`index:${lang}`)
        }
        if (altText) {
          dispatch({type: ContentEditActionEnum.update, path: ['altText', lang], value: altText})
          received.add(`altText:${lang}`)
        }
        if (title) {
          dispatch({type: ContentEditActionEnum.update, path: ['title', lang], value: title})
          received.add(`title:${lang}`)
        }
      })

      const missing = expected.filter(({field, lang}) => !received.has(`${field}:${lang}`))

      // The source Trados was asked to translate, as the API saw it: usually
      // the default language's index, but the original of a misplaced PDF when
      // that was the only one in a site language.
      const sourceLang = indexSourceLang ?? DEFAULT_LANGUAGE
      const indexSource =
        results.find(({lang}) => lang === sourceLang)?.index ?? indexByLang[sourceLang] ?? ''

      setWarnings(buildTranslationWarnings(missing, indexSource.length, sourceLang, langUi, t))
      // Without these a PDF the API could not read fills nothing in and the
      // button looks like it did nothing.
      setReadErrors(
        failedPdfs.map(({lang, reason}) =>
          t('media.pdfReadFailed', 'The PDF uploaded for {{lang}} could not be read: {{reason}}', {
            lang,
            reason,
          })
        )
      )
      setNotices(
        misplacedPdfs.map(({lang, detectedLang}) =>
          t(
            'media.pdfLanguageMismatch',
            'The PDF uploaded for {{lang}} is in {{detectedLang}}. Its text was added to the {{detectedLang}} index, and {{lang}} is translated from it like a language without a PDF.',
            {lang, detectedLang}
          )
        )
      )
    } catch (err) {
      console.error(err)
      setError(t('media.indexParseFailed', 'Failed to parse the PDF.'))
    } finally {
      setLoading(false)
    }
  }

  // Only the default language's index matters here: it is the source text, and
  // its length decides whether Trados can translate it into the others at all.
  const indexCharCount = (content?.index?.[DEFAULT_LANGUAGE] ?? '').length

  return (
    <Grid>
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 10,
        }}>
        <Button
          appearance="primary"
          disabled={disabled || loading}
          loading={loading}
          onClick={() => {
            fillIndex().catch((e) => console.error(e))
          }}>
          {t('media.fillIndex', 'Create index from PDF')}
        </Button>
        {error && (
          <Message type="error" showIcon>
            {error}
          </Message>
        )}
        {readErrors.map((message) => (
          <Message key={message} type="error" showIcon>
            {message}
          </Message>
        ))}
        {notices.map((message) => (
          <Message key={message} type="info" showIcon>
            {message}
          </Message>
        ))}
        {warnings.map((message) => (
          <Message key={message} type="warning" showIcon>
            {message}
          </Message>
        ))}
        {/* Standing indicator for an index that is already too long, so the
            limit is visible before the button is pressed. Suppressed once a run
            has produced its own warnings, which say the same thing with the
            concrete languages. */}
        {warnings.length === 0 && indexCharCount > TRADOS_TRANSLATION_CHAR_LIMIT && (
          <Message type="warning" showIcon>
            {t(
              'media.indexOverLimit',
              'The {{lang}} index has {{chars}} characters, over the {{limit}}-character translation limit. Languages without their own PDF cannot be filled by translation.',
              {
                lang: DEFAULT_LANGUAGE,
                chars: formatCharCount(indexCharCount, langUi),
                limit: formatCharCount(TRADOS_TRANSLATION_CHAR_LIMIT, langUi),
              }
            )}
          </Message>
        )}
        <div style={{fontSize: 12, color: '#8e8e93'}}>
          {t('media.indexCharCount', 'Index characters')} ({DEFAULT_LANGUAGE}):{' '}
          {formatCharCount(indexCharCount, langUi)} /{' '}
          {formatCharCount(TRADOS_TRANSLATION_CHAR_LIMIT, langUi)}
        </div>
      </div>
      <Panel
        collapsible
        defaultExpanded
        header={<div style={{fontWeight: 'bold'}}>{t('content.panels.contentSectionTitle')}</div>}
        bordered
        style={{
          overflow: 'visible',
        }}>
        <BlockObject
          model={{
            type: 'object' as any,
            fields: contentModelConfigMerged.schema.content,
          }}
          value={content}
          schemaPath={[]}
          configs={configs}
          dispatch={dispatch}
          languageContext={{
            langLane1: langLaneL,
            langLane2: langLaneR,
            languagesConfig: configs.apiConfig.languages,
            langUi: langUi,
          }}></BlockObject>
      </Panel>
    </Grid>
  )
}
